import React, { useState, useMemo, useEffect } from "react";
import { Card, Table, Row, Col, Form, Button, Dropdown, Badge, Modal, Spinner, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { fetchAlertesList } from "../services/api";

// ============================================
// UTILITAIRES
// ============================================
const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle-fill';
    case 'error': return 'bi-exclamation-circle-fill';
    case 'warning': return 'bi-exclamation-triangle-fill';
    case 'info': return 'bi-info-circle-fill';
    default: return 'bi-bell-fill';
  }
};

const getNotificationBg = (type) => {
  switch (type) {
    case 'success': return 'success';
    case 'error': return 'danger';
    case 'warning': return 'warning';
    case 'info': return 'info';
    default: return 'primary';
  }
};

const getAlertIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle';
    case 'danger': return 'bi-exclamation-triangle';
    case 'warning': return 'bi-exclamation-triangle';
    case 'info': return 'bi-info-circle';
    default: return 'bi-bell';
  }
};

export default function AlertesManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedAlertes, setSelectedAlertes] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [error, setError] = useState("");
  const [alertes, setAlertes] = useState([]);
  const [loadingAlertes, setLoadingAlertes] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });
  const [selectedAlerte, setSelectedAlerte] = useState(null);

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => hideNotification(id), duration);
  };

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => hideAlert(), duration);
  };

  const hideAlert = () => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  };

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    loadAlertes();
  }, []);

  const loadAlertes = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) setRefreshing(true);
      else setLoadingAlertes(true);
      setError("");

      const token = localStorage.getItem('authToken');
      if (!token) {
        setError("Token d'authentification non trouvé");
        showAlert("Token d'authentification non trouvé. Veuillez vous reconnecter.", 'danger');
        return;
      }

      const alertesData = await fetchAlertesList(token);

      if (!alertesData || !alertesData.data || !Array.isArray(alertesData.data)) {
        setError("Format de données invalide reçu du serveur");
        showAlert("Format de données invalide reçu du serveur", 'danger');
        return;
      }

      const formattedAlertes = alertesData.data.map(alerte => {
        let payloadData = {};
        try {
          payloadData = alerte.payload ? JSON.parse(alerte.payload) : {};
        } catch (e) {
          console.warn('❌ Erreur parsing payload:', e);
        }

        return {
          id: alerte.id,
          business_id: alerte.business_id,
          type_alerte: alerte.type_alerte || '',
          message: alerte.message || '',
          statut: alerte.statut || '',
          created_at: alerte.created_at || '',
          date_lcl_alerte: alerte.date_lcl_alerte || '',
          updated_at: alerte.updated_at || '',
          payload: payloadData,
          balance_apres: payloadData.balance_apres || 0,
          seuil: payloadData.seuil || '0.00',
          business_name: alerte.business_name || 'Inconnue'
        };
      });

      setAlertes(formattedAlertes);
      if (showRefreshSpinner) {
        showNotification('Liste des alertes actualisée', 'success', 3000);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des alertes:', error);
      const errorMessage = error.message || "Erreur lors du chargement des alertes";
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoadingAlertes(false);
      setRefreshing(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const getTypeAlerteLabel = (type) => {
    const types = {
      'SEUIL_SOLDE': 'Seuil de solde',
      'SEUIL_CONSO_JOUR': 'Seuil consommation journalière',
      'SEUIL_CONSO_MOIS': 'Seuil consommation mensuelle',
      'ALERTE_SECURITE': 'Alerte sécurité',
      'NOTIFICATION_SYSTEME': 'Notification système'
    };
    return types[type] || type;
  };

  const getTypeAlerteBadge = (type) => {
    const colors = {
      'SEUIL_SOLDE': 'warning',
      'SEUIL_CONSO_JOUR': 'info',
      'SEUIL_CONSO_MOIS': 'primary',
      'ALERTE_SECURITE': 'danger',
      'NOTIFICATION_SYSTEME': 'secondary'
    };
    return colors[type] || 'secondary';
  };

  const getStatutBadge = (statut) => {
    const colors = {
      'ENVOYEE': 'success',
      'EN_ATTENTE': 'warning',
      'ECHEC': 'danger',
      'EN_COURS': 'info'
    };
    return colors[statut] || 'secondary';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'ENVOYEE': 'Envoyée',
      'EN_ATTENTE': 'En attente',
      'ECHEC': 'Échec',
      'EN_COURS': 'En cours'
    };
    return labels[statut] || statut;
  };

  const getStatutClass = (statut) => {
    switch (statut) {
      case 'ENVOYEE': return 'al-status-success';
      case 'EN_ATTENTE': return 'al-status-warning';
      case 'ECHEC': return 'al-status-danger';
      case 'EN_COURS': return 'al-status-info';
      default: return 'al-status-secondary';
    }
  };

  const getTypeClass = (type) => {
    switch (type) {
      case 'SEUIL_SOLDE': return 'al-type-warning';
      case 'SEUIL_CONSO_JOUR': return 'al-type-cyan';
      case 'SEUIL_CONSO_MOIS': return 'al-type-blue';
      case 'ALERTE_SECURITE': return 'al-type-danger';
      default: return 'al-type-gray';
    }
  };

  const handleShowDetails = (alerte) => {
    setSelectedAlerte(alerte);
    setShowDetailsModal(true);
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = alertes.filter(item => {
      const searchTerm = filterText.toLowerCase();
      if (!searchTerm.trim()) return true;

      return (
        (item.type_alerte && item.type_alerte.toLowerCase().includes(searchTerm)) ||
        (item.message && item.message.toLowerCase().includes(searchTerm)) ||
        (item.statut && item.statut.toLowerCase().includes(searchTerm)) ||
        (item.business_name && item.business_name.toLowerCase().includes(searchTerm)) ||
        (getTypeAlerteLabel(item.type_alerte).toLowerCase().includes(searchTerm))
      );
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [alertes, filterText, sortField, sortDirection]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = alertes.length;
    const envoyees = alertes.filter(a => a.statut === 'ENVOYEE').length;
    const enEchec = alertes.filter(a => a.statut === 'ECHEC').length;
    const enAttente = alertes.filter(a => a.statut === 'EN_ATTENTE').length;
    const seuilSolde = alertes.filter(a => a.type_alerte === 'SEUIL_SOLDE').length;
    const autresTypes = total - seuilSolde;
    return { total, envoyees, enEchec, enAttente, seuilSolde, autresTypes };
  }, [alertes]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedAlertes(currentItems.map(a => a.id));
    else setSelectedAlertes([]);
  };

  const handleSelectAlerte = (alerteId) => {
    setSelectedAlertes(prev =>
      prev.includes(alerteId)
        ? prev.filter(id => id !== alerteId)
        : [...prev, alerteId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedAlertes.length === currentItems.length;
  const isSomeSelected = selectedAlertes.length > 0 && selectedAlertes.length < currentItems.length;

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedAlertes.length > 0
      ? alertes.filter(a => selectedAlertes.includes(a.id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      showAlert("Aucune donnée à exporter", 'warning', 4000);
      setExportLoading(false);
      return;
    }

    try {
      const enhancedData = dataToExport.map(alerte => ({
        ...alerte,
        type_alerte_label: getTypeAlerteLabel(alerte.type_alerte),
        statut_label: getStatutLabel(alerte.statut),
        date_alerte: alerte.date_lcl_alerte,
        solde_apres: alerte.balance_apres,
        seuil_config: alerte.seuil,
        business_name: alerte.business_name
      }));

      if (format === "csv") {
        const headers = ["ID","Type Alerte","Message","Statut","Date Création","Date Alerte","Solde Après","Seuil"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...enhancedData.map(a => [
            a.id, a.type_alerte_label, a.message, a.statut_label,
            a.created_at, a.date_alerte, a.solde_apres, a.seuil_config
          ].map(v => `"${v || ''}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, "text/csv;charset=utf-8;", `export-alertes-${Date.now()}.csv`);
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(enhancedData.map(a => ({
          ID: a.id,
          "Type Alerte": a.type_alerte_label,
          Message: a.message,
          Statut: a.statut_label,
          "Date Création": a.created_at,
          "Date Alerte": a.date_alerte,
          "Solde Après": a.solde_apres,
          "Seuil Configuré": a.seuil_config
        })));
        ws['!cols'] = [
          { wch: 8 }, { wch: 20 }, { wch: 25 }, { wch: 12 },
          { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Alertes");
        XLSX.writeFile(wb, `export-alertes-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(enhancedData, null, 2), "application/json;charset=utf-8;", `export-alertes-${Date.now()}.json`);
      } else if (format === "pdf") {
        generatePDF(enhancedData);
      }

      showNotification(`Export ${format.toUpperCase()} réussi - ${dataToExport.length} enregistrement(s)`, 'success', 4000);
    } catch (error) {
      showAlert(`Erreur lors de l'export: ${error.message}`, 'error', 6000);
    } finally {
      setExportLoading(false);
    }
  };

  const generatePDF = (data) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = margin;

      const primaryColor = [44, 62, 120];
      const softGray = [245, 246, 248];
      const darkText = [40, 40, 40];
      const borderGray = [210, 214, 220];

      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 30, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("HISTORIQUE DES ALERTES", pageWidth / 2, 12, { align: "center" });

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(`Généré le ${new Date().toLocaleDateString("fr-FR")} • ${data.length} alerte(s)`, pageWidth / 2, 20, { align: "center" });

      yPosition = 40;

      const headers = ["TYPE", "MESSAGE", "STATUT", "DATE", "SOLDE", "SEUIL"];
      const columnWidths = [35, 50, 20, 25, 20, 20];
      const totalWidth = columnWidths.reduce((a, b) => a + b);

      const drawTableHeader = () => {
        doc.setFillColor(...primaryColor);
        doc.rect(margin, yPosition, totalWidth, 9, "F");
        let x = margin;
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, "bold");
        headers.forEach((head, i) => {
          doc.text(head, x + 2, yPosition + 6);
          x += columnWidths[i];
        });
        yPosition += 9;
      };

      drawTableHeader();
      doc.setFontSize(7);
      doc.setFont(undefined, "normal");

      data.forEach((row, index) => {
        if (yPosition > 265) {
          doc.addPage();
          yPosition = 20;
          drawTableHeader();
        }
        if (index % 2 === 0) {
          doc.setFillColor(...softGray);
          doc.rect(margin, yPosition, totalWidth, 7, "F");
        }
        let x = margin;
        doc.setTextColor(...darkText);
        const rowData = [
          row.type_alerte_label || "-",
          row.message || "-",
          row.statut_label || "-",
          row.date_alerte || "-",
          row.solde_apres ? row.solde_apres.toString() : "-",
          row.seuil_config || "-"
        ];
        rowData.forEach((cell, i) => {
          let text = cell.toString();
          const limit = [25, 35, 12, 12, 10, 10][i];
          if (text.length > limit) text = text.substring(0, limit - 3) + "...";
          doc.text(text, x + 2, yPosition + 5);
          x += columnWidths[i];
        });
        yPosition += 7;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(...borderGray);
        doc.line(margin, 283, pageWidth - margin, 283);
        doc.setFontSize(7);
        doc.setTextColor(110, 110, 110);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, 289, { align: "center" });
      }

      doc.save(`alertes-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("❌ Erreur PDF:", error);
      throw new Error(`Erreur PDF: ${error.message}`);
    }
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  const SortableHeader = ({ field, children }) => (
    <th className="al-sortable-header" onClick={() => handleSort(field)}>
      <span className="al-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill al-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="al-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {notifications.map((notification) => {
          const bgColor = getNotificationBg(notification.type);
          const icon = getNotificationIcon(notification.type);
          return (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={8000}
              autohide
              bg={bgColor}
            >
              <Toast.Header className={`bg-${bgColor} text-white`}>
                <i className={`bi ${icon} me-2`}></i>
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' :
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">{notification.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>

      {/* ========== ALERT ========== */}
      {alertMessage.show && (
        <div className={`al-alert al-alert-${alertMessage.type} mb-4`}>
          <i className={`bi ${getAlertIcon(alertMessage.type)}`}></i>
          <div className="flex-grow-1">{alertMessage.message}</div>
          <button className="al-alert-close" onClick={hideAlert}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="al-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="al-header-icon">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div>
              <h3 className="al-header-title mb-1">Historique des alertes</h3>
              <p className="al-header-subtitle mb-0">
                Consultation et gestion des alertes système
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Dropdown>
              <Dropdown.Toggle className="al-btn-secondary" disabled={exportLoading}>
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat al-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="al-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button className="al-btn-primary" onClick={() => loadAlertes(true)} disabled={refreshing}>
              <i className={`bi ${refreshing ? 'bi-arrow-repeat al-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>{refreshing ? 'Actualisation...' : 'Actualiser'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="al-alert al-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <Button variant="outline-danger" size="sm" onClick={() => loadAlertes(true)}>
            <i className="bi bi-arrow-clockwise me-1"></i>Réessayer
          </Button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={4} lg={2}>
          <div className="al-stat-card">
            <div className="al-stat-icon-wrapper al-stat-icon-blue">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div className="al-stat-content">
              <div className="al-stat-label">Total</div>
              <div className="al-stat-value">{stats.total}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="al-stat-card">
            <div className="al-stat-icon-wrapper al-stat-icon-green">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div className="al-stat-content">
              <div className="al-stat-label">Envoyées</div>
              <div className="al-stat-value">{stats.envoyees}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="al-stat-card">
            <div className="al-stat-icon-wrapper al-stat-icon-amber">
              <i className="bi bi-clock-fill"></i>
            </div>
            <div className="al-stat-content">
              <div className="al-stat-label">En attente</div>
              <div className="al-stat-value">{stats.enAttente}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="al-stat-card">
            <div className="al-stat-icon-wrapper al-stat-icon-red">
              <i className="bi bi-x-circle-fill"></i>
            </div>
            <div className="al-stat-content">
              <div className="al-stat-label">Échecs</div>
              <div className="al-stat-value">{stats.enEchec}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="al-stat-card">
            <div className="al-stat-icon-wrapper al-stat-icon-cyan">
              <i className="bi bi-graph-up-arrow"></i>
            </div>
            <div className="al-stat-content">
              <div className="al-stat-label">Seuil solde</div>
              <div className="al-stat-value">{stats.seuilSolde}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="al-stat-card">
            <div className="al-stat-icon-wrapper al-stat-icon-gray">
              <i className="bi bi-shield-exclamation"></i>
            </div>
            <div className="al-stat-content">
              <div className="al-stat-label">Autres</div>
              <div className="al-stat-value">{stats.autresTypes}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="al-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={7} md={12}>
            <div className="al-search-wrapper">
              <i className="bi bi-search al-search-icon"></i>
              <input
                type="text"
                className="al-search-input"
                placeholder="Rechercher par type, message, statut, entreprise..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="al-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="al-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
              <option value={100}>100 par page</option>
            </select>
          </Col>
          <Col lg={2} md={6}>
            <button
              className="al-btn-secondary w-100"
              onClick={() => loadAlertes(true)}
              disabled={refreshing}
            >
              <i className={`bi ${refreshing ? 'bi-arrow-repeat al-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="al-table-card">
        {loadingAlertes ? (
          <div className="al-loading">
            <div className="al-loading-spinner"></div>
            <div className="al-loading-text">Chargement des alertes...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="al-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="al-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="al-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="business_name">
                      <i className="bi bi-building me-1"></i>Entreprise
                    </SortableHeader>
                    <SortableHeader field="type_alerte">
                      <i className="bi bi-tag me-1"></i>Type d'alerte
                    </SortableHeader>
                    <SortableHeader field="statut">
                      <i className="bi bi-flag me-1"></i>Statut
                    </SortableHeader>
                    <SortableHeader field="created_at">
                      <i className="bi bi-calendar me-1"></i>Date création
                    </SortableHeader>
                    <SortableHeader field="date_lcl_alerte">
                      <i className="bi bi-calendar-event me-1"></i>Date alerte
                    </SortableHeader>
                    <th><i className="bi bi-coin me-1"></i>Solde après</th>
                    <th><i className="bi bi-sliders me-1"></i>Seuil</th>
                    <th className="text-end" style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((alerte) => (
                      <tr key={alerte.id} className="al-row">
                        <td>
                          <div className="al-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="al-checkbox"
                              checked={selectedAlertes.includes(alerte.id)}
                              onChange={() => handleSelectAlerte(alerte.id)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="al-business-cell">
                            <div className="al-business-avatar">
                              {alerte.business_name?.charAt(0).toUpperCase() || '?'}
                            </div>
                            <span className="al-business-name">{alerte.business_name}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`al-type-badge ${getTypeClass(alerte.type_alerte)}`}>
                            <i className="bi bi-lightning-charge-fill"></i>
                            {getTypeAlerteLabel(alerte.type_alerte)}
                          </span>
                        </td>
                        <td>
                          <span className={`al-status-badge ${getStatutClass(alerte.statut)}`}>
                            <i className={`bi ${
                              alerte.statut === 'ENVOYEE' ? 'bi-check-circle-fill' :
                              alerte.statut === 'ECHEC' ? 'bi-x-circle-fill' :
                              alerte.statut === 'EN_ATTENTE' ? 'bi-clock-fill' :
                              'bi-arrow-repeat'
                            }`}></i>
                            {getStatutLabel(alerte.statut)}
                          </span>
                        </td>
                        <td>
                          <span className="al-date-cell">
                            <i className="bi bi-clock-history"></i>
                            {formatDate(alerte.created_at)}
                          </span>
                        </td>
                        <td>
                          <span className="al-date-cell">
                            <i className="bi bi-calendar-event"></i>
                            {formatDate(alerte.date_lcl_alerte)}
                          </span>
                        </td>
                        <td>
                          <code className="al-code">{alerte.balance_apres || 'N/A'}</code>
                        </td>
                        <td>
                          <code className="al-code-warning">{alerte.seuil || 'N/A'}</code>
                        </td>
                        <td className="text-end">
                          <div className="al-actions">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                              <button
                                className="al-action-btn al-action-view"
                                onClick={() => handleShowDetails(alerte)}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="al-empty-state">
                          <div className="al-empty-icon">
                            <i className="bi bi-bell-slash"></i>
                          </div>
                          <h5 className="al-empty-title">Aucune alerte trouvée</h5>
                          <p className="al-empty-text">
                            {filterText
                              ? "Aucune alerte ne correspond à votre recherche"
                              : "Aucune alerte n'a encore été enregistrée"}
                          </p>
                          {filterText && (
                            <button className="al-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>Réinitialiser
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="al-pagination">
                <div className="al-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedAlertes.length > 0 && (
                    <span className="al-pagination-selected">
                      • <strong>{selectedAlertes.length}</strong> sélectionnée{selectedAlertes.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="al-pagination-controls">
                  <button className="al-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="al-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;
                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`al-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                  <button className="al-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="al-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL DÉTAILS ========== */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="al-modal">
        <div className="al-modal-header al-modal-header-info">
          <div className="d-flex align-items-center gap-3">
            <div className="al-modal-icon">
              <i className="bi bi-bell-fill"></i>
            </div>
            <div>
              <h5 className="al-modal-title mb-0">Détails de l'alerte</h5>
              <small className="al-modal-subtitle">Informations complètes</small>
            </div>
          </div>
          <button className="al-modal-close" onClick={() => setShowDetailsModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="al-modal-body">
          {selectedAlerte && (
            <>
              {/* HERO */}
              <div className="al-detail-hero">
                <div className="al-detail-avatar">
                  <i className="bi bi-bell-fill"></i>
                </div>
                <div className="al-detail-hero-content">
                  <div className="al-detail-hero-name">
                    {getTypeAlerteLabel(selectedAlerte.type_alerte)}
                  </div>
                  <div className="al-detail-hero-meta">
                    <span><i className="bi bi-building"></i>{selectedAlerte.business_name}</span>
                    <span><i className="bi bi-hash"></i>ID: {selectedAlerte.id}</span>
                  </div>
                </div>
                <span className={`al-status-badge ${getStatutClass(selectedAlerte.statut)}`}>
                  <i className={`bi ${
                    selectedAlerte.statut === 'ENVOYEE' ? 'bi-check-circle-fill' :
                    selectedAlerte.statut === 'ECHEC' ? 'bi-x-circle-fill' :
                    'bi-clock-fill'
                  }`}></i>
                  {getStatutLabel(selectedAlerte.statut)}
                </span>
              </div>

              {/* MESSAGE */}
              <div className="al-detail-message">
                <i className="bi bi-chat-square-text"></i>
                <div>
                  <div className="al-detail-message-label">Message</div>
                  <div className="al-detail-message-text">{selectedAlerte.message || 'Aucun message'}</div>
                </div>
              </div>

              {/* GRID */}
              <div className="al-detail-grid">
                <div className="al-detail-card">
                  <div className="al-detail-card-icon al-detail-card-icon-blue">
                    <i className="bi bi-calendar"></i>
                  </div>
                  <div className="al-detail-card-content">
                    <div className="al-detail-card-label">Créé le</div>
                    <div className="al-detail-card-value">
                      {selectedAlerte.created_at ? new Date(selectedAlerte.created_at).toLocaleString('fr-FR') : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="al-detail-card">
                  <div className="al-detail-card-icon al-detail-card-icon-amber">
                    <i className="bi bi-calendar-event"></i>
                  </div>
                  <div className="al-detail-card-content">
                    <div className="al-detail-card-label">Date alerte</div>
                    <div className="al-detail-card-value">
                      {selectedAlerte.date_lcl_alerte ? new Date(selectedAlerte.date_lcl_alerte).toLocaleDateString('fr-FR') : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="al-detail-card">
                  <div className="al-detail-card-icon al-detail-card-icon-cyan">
                    <i className="bi bi-coin"></i>
                  </div>
                  <div className="al-detail-card-content">
                    <div className="al-detail-card-label">Solde après</div>
                    <div className="al-detail-card-value al-value-cyan">{selectedAlerte.balance_apres || 'N/A'}</div>
                  </div>
                </div>

                <div className="al-detail-card">
                  <div className="al-detail-card-icon al-detail-card-icon-amber">
                    <i className="bi bi-sliders"></i>
                  </div>
                  <div className="al-detail-card-content">
                    <div className="al-detail-card-label">Seuil configuré</div>
                    <div className="al-detail-card-value al-value-amber">{selectedAlerte.seuil || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* PAYLOAD */}
              <div className="al-detail-section">
                <div className="al-detail-section-header">
                  <i className="bi bi-code-square"></i>
                  <span>Payload (JSON)</span>
                </div>
                <div className="al-detail-section-body">
                  <pre className="al-code-block">
                    {JSON.stringify(selectedAlerte.payload, null, 2)}
                  </pre>
                </div>
              </div>

              {/* INFO TECHNIQUE */}
              <div className="al-detail-section mt-3">
                <div className="al-detail-section-header">
                  <i className="bi bi-info-circle"></i>
                  <span>Informations techniques</span>
                </div>
                <div className="al-detail-section-body">
                  <div className="al-tech-row">
                    <span className="al-tech-label">Business ID</span>
                    <span className="al-tech-value">{selectedAlerte.business_id || 'N/A'}</span>
                  </div>
                  <div className="al-tech-row">
                    <span className="al-tech-label">Dernière modification</span>
                    <span className="al-tech-value">
                      {selectedAlerte.updated_at ? new Date(selectedAlerte.updated_at).toLocaleString('fr-FR') : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <div className="al-modal-footer">
          <button className="al-btn-cancel" onClick={() => setShowDetailsModal(false)}>
            Fermer
          </button>
        </div>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .al-page { background: #f8fafc; min-height: 100vh; }
        .al-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* ========== HEADER ========== */
        .al-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .al-header-icon {
          width: 56px; height: 56px; border-radius: 14px;
          background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px; box-shadow: 0 8px 20px -6px rgba(245, 158, 11, 0.5);
        }
        .al-header-title {
          font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em;
        }
        .al-header-subtitle { color: #64748b; font-size: 0.875rem; }

        /* ========== BUTTONS ========== */
        .al-btn-primary, .al-btn-secondary, .al-btn-cancel {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 20px; border-radius: 10px; font-weight: 600; font-size: 0.875rem;
          border: none; transition: all 0.2s; cursor: pointer; text-decoration: none;
        }
        .al-btn-primary {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white;
        }
        .al-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(245, 158, 11, 0.4);
        }
        .al-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .al-btn-secondary {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .al-btn-secondary:hover:not(:disabled) {
          background: #f8fafc; border-color: #cbd5e1; color: #0f172a;
        }
        .al-btn-secondary:disabled { opacity: 0.6; }
        .al-btn-cancel {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .al-btn-cancel:hover { background: #f1f5f9; }
        .al-dropdown-menu {
          border: 1px solid #e2e8f0; border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1); padding: 6px;
        }
        .al-dropdown-menu .dropdown-item {
          border-radius: 6px; padding: 8px 12px; font-size: 0.85rem; display: flex; align-items: center;
        }
        .al-dropdown-menu .dropdown-item:hover { background: #f1f5f9; }

        /* ========== ALERTS ========== */
        .al-alert {
          display: flex; align-items: center; gap: 12px; padding: 14px 18px;
          border-radius: 12px; font-size: 0.85rem; font-weight: 500;
        }
        .al-alert > i { font-size: 1.3rem; flex-shrink: 0; }
        .al-alert-success { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); color: #065f46; border-left: 4px solid #10b981; }
        .al-alert-danger { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); color: #991b1b; border-left: 4px solid #ef4444; }
        .al-alert-warning { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); color: #92400e; border-left: 4px solid #f59e0b; }
        .al-alert-info { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); color: #1e40af; border-left: 4px solid #3b82f6; }
        .al-alert-close {
          background: none; border: none; color: inherit; opacity: 0.6; cursor: pointer;
          padding: 4px 8px; border-radius: 6px; font-size: 1rem;
        }
        .al-alert-close:hover { opacity: 1; background: rgba(0,0,0,0.05); }

        /* ========== STATS ========== */
        .al-stat-card {
          display: flex; align-items: center; gap: 12px; background: white;
          border-radius: 14px; padding: 16px; border: 1px solid #e2e8f0;
          transition: all 0.25s; height: 100%;
        }
        .al-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08); }
        .al-stat-icon-wrapper {
          width: 42px; height: 42px; border-radius: 11px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
        }
        .al-stat-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .al-stat-icon-green { background: #d1fae5; color: #10b981; }
        .al-stat-icon-amber { background: #fef3c7; color: #d97706; }
        .al-stat-icon-red   { background: #fee2e2; color: #ef4444; }
        .al-stat-icon-cyan  { background: #cffafe; color: #0891b2; }
        .al-stat-icon-gray  { background: #f1f5f9; color: #64748b; }
        .al-stat-content { min-width: 0; flex-grow: 1; }
        .al-stat-label {
          font-size: 0.65rem; color: #64748b; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
        }
        .al-stat-value {
          font-size: 1.35rem; font-weight: 700; color: #0f172a;
          letter-spacing: -0.02em; line-height: 1;
        }

        /* ========== FILTRES ========== */
        .al-filters {
          background: white; border-radius: 14px; padding: 16px 20px; border: 1px solid #e2e8f0;
        }
        .al-search-wrapper { position: relative; display: flex; align-items: center; }
        .al-search-icon {
          position: absolute; left: 14px; color: #94a3b8; font-size: 1.05rem; pointer-events: none;
        }
        .al-search-input {
          width: 100%; padding: 11px 40px 11px 42px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; background: #f8fafc;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .al-search-input:focus {
          background: white; border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }
        .al-search-clear {
          position: absolute; right: 8px; background: none; border: none; color: #94a3b8;
          font-size: 1rem; padding: 6px 10px; cursor: pointer; border-radius: 6px; transition: all 0.15s;
        }
        .al-search-clear:hover { background: #fee2e2; color: #ef4444; }
        .al-select {
          width: 100%; padding: 11px 14px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; font-weight: 500;
          background: #f8fafc; color: #334155; outline: none; cursor: pointer; font-family: inherit;
        }
        .al-select:focus {
          background: white; border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        /* ========== TABLE ========== */
        .al-table-card {
          background: white; border-radius: 16px; border: 1px solid #e2e8f0;
          overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .al-table { font-size: 0.875rem; }
        .al-table thead th {
          background: #f8fafc; color: #475569; font-weight: 700; font-size: 0.7rem;
          text-transform: uppercase; letter-spacing: 0.05em; padding: 16px;
          border-bottom: 1px solid #e2e8f0; border-top: none; white-space: nowrap;
        }
        .al-table tbody td {
          padding: 16px; vertical-align: middle; border-bottom: 1px solid #f1f5f9;
        }
        .al-row { transition: background-color 0.15s; }
        .al-row:hover { background-color: #f8fafc; }
        .al-sortable-header { cursor: pointer; user-select: none; }
        .al-sortable-header:hover { color: #f59e0b; }
        .al-sortable-content { display: inline-flex; align-items: center; gap: 6px; }
        .al-sort-icon { color: #f59e0b; font-size: 0.75rem; }

        .al-checkbox-wrapper { display: flex; align-items: center; justify-content: center; }
        .al-checkbox {
          width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid #cbd5e1;
          cursor: pointer; accent-color: #f59e0b;
        }

        /* Business cell */
        .al-business-cell { display: flex; align-items: center; gap: 10px; }
        .al-business-avatar {
          width: 36px; height: 36px; border-radius: 10px;
          background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 14px; flex-shrink: 0;
        }
        .al-business-name { font-weight: 700; color: #0f172a; }

        /* Type badge */
        .al-type-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 6px;
          font-size: 0.7rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.03em;
        }
        .al-type-warning { background: #fef3c7; color: #92400e; }
        .al-type-cyan    { background: #cffafe; color: #0891b2; }
        .al-type-blue    { background: #dbeafe; color: #1e40af; }
        .al-type-danger  { background: #fee2e2; color: #b91c1c; }
        .al-type-gray    { background: #f1f5f9; color: #64748b; }

        /* Status badge */
        .al-status-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 11px; border-radius: 6px;
          font-weight: 700; font-size: 0.72rem; white-space: nowrap;
        }
        .al-status-success { background: #d1fae5; color: #059669; }
        .al-status-warning { background: #fef3c7; color: #92400e; }
        .al-status-danger  { background: #fee2e2; color: #b91c1c; }
        .al-status-info    { background: #cffafe; color: #0891b2; }
        .al-status-secondary { background: #f1f5f9; color: #64748b; }

        /* Date cell */
        .al-date-cell {
          display: inline-flex; align-items: center; gap: 6px;
          color: #64748b; font-size: 0.8rem; font-variant-numeric: tabular-nums;
        }
        .al-date-cell i { color: #94a3b8; }

        /* Codes */
        .al-code {
          font-size: 0.78rem; color: #1e40af; background: #eff6ff;
          padding: 4px 8px; border-radius: 5px; font-weight: 600;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }
        .al-code-warning {
          font-size: 0.78rem; color: #92400e; background: #fef3c7;
          padding: 4px 8px; border-radius: 5px; font-weight: 600;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        /* Actions */
        .al-actions { display: inline-flex; gap: 6px; justify-content: flex-end; }
        .al-action-btn {
          width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e2e8f0;
          background: white; color: #64748b; display: inline-flex;
          align-items: center; justify-content: center; cursor: pointer;
          transition: all 0.15s; font-size: 0.9rem;
        }
        .al-action-view:hover {
          background: #06b6d4; border-color: #06b6d4; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .al-loading { padding: 80px 20px; text-align: center; }
        .al-loading-spinner {
          width: 48px; height: 48px; border: 4px solid #e2e8f0;
          border-top-color: #f59e0b; border-radius: 50%;
          animation: al-spin 0.8s linear infinite; margin: 0 auto 16px;
        }
        .al-loading-text { color: #64748b; font-size: 0.875rem; font-weight: 500; }
        .al-empty-state { padding: 60px 20px; text-align: center; }
        .al-empty-icon {
          width: 80px; height: 80px; border-radius: 50%; background: #f1f5f9; color: #94a3b8;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; margin-bottom: 20px;
        }
        .al-empty-title { color: #334155; font-weight: 700; margin-bottom: 8px; }
        .al-empty-text { color: #94a3b8; margin-bottom: 20px; }
        .al-btn-empty-reset {
          display: inline-flex; align-items: center; background: white; color: #f59e0b;
          border: 1px solid #fde68a; border-radius: 8px; font-weight: 600;
          padding: 8px 16px; cursor: pointer; transition: all 0.2s;
        }
        .al-btn-empty-reset:hover { background: #f59e0b; color: white; border-color: #f59e0b; }

        /* ========== PAGINATION ========== */
        .al-pagination {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 24px; background: #f8fafc; border-top: 1px solid #e2e8f0;
          flex-wrap: wrap; gap: 12px;
        }
        .al-pagination-info {
          font-size: 0.8rem; color: #64748b; display: flex; align-items: center; gap: 6px;
        }
        .al-pagination-info strong { color: #0f172a; }
        .al-pagination-selected { color: #f59e0b; margin-left: 8px; }
        .al-pagination-controls { display: flex; align-items: center; gap: 6px; }
        .al-page-btn {
          min-width: 34px; height: 34px; padding: 0 10px; border-radius: 8px;
          border: 1px solid #e2e8f0; background: white; color: #475569;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s; font-size: 0.8rem; font-weight: 600;
        }
        .al-page-btn:hover:not(:disabled) { background: #f59e0b; color: white; border-color: #f59e0b; }
        .al-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .al-page-btn.active { background: #f59e0b; color: white; border-color: #f59e0b; }

        /* ========== MODAL ========== */
        .al-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
        .al-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; color: white;
        }
        .al-modal-header-info { background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%); }
        .al-modal-icon {
          width: 44px; height: 44px; border-radius: 10px; background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center; font-size: 20px;
        }
        .al-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
        .al-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
        .al-modal-close {
          background: rgba(255,255,255,0.1); border: none; color: white;
          width: 36px; height: 36px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 1rem;
        }
        .al-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
        .al-modal-body {
          padding: 24px; background: #f8fafc; max-height: 70vh; overflow-y: auto;
        }
        .al-modal-footer {
          padding: 16px 24px; background: white; border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px;
        }

        /* Detail Hero */
        .al-detail-hero {
          display: flex; align-items: center; gap: 16px; padding: 20px;
          background: white; border-radius: 14px; border: 1px solid #e2e8f0;
          margin-bottom: 16px; flex-wrap: wrap;
        }
        .al-detail-avatar {
          width: 60px; height: 60px; border-radius: 16px;
          background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 24px; flex-shrink: 0;
          box-shadow: 0 8px 16px -4px rgba(245, 158, 11, 0.3);
        }
        .al-detail-hero-content { flex-grow: 1; min-width: 0; }
        .al-detail-hero-name {
          font-weight: 700; color: #0f172a; font-size: 1.05rem; margin-bottom: 6px;
        }
        .al-detail-hero-meta {
          display: flex; flex-wrap: wrap; gap: 14px; font-size: 0.8rem; color: #64748b;
        }
        .al-detail-hero-meta span { display: inline-flex; align-items: center; gap: 5px; }
        .al-detail-hero-meta i { color: #94a3b8; }

        /* Message box */
        .al-detail-message {
          display: flex; gap: 12px; padding: 16px 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 12px; border-left: 4px solid #3b82f6;
          margin-bottom: 16px;
        }
        .al-detail-message > i {
          color: #3b82f6; font-size: 1.3rem; flex-shrink: 0; margin-top: 2px;
        }
        .al-detail-message-label {
          font-size: 0.68rem; color: #1e40af; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .al-detail-message-text {
          color: #1e293b; font-size: 0.875rem; font-weight: 500; line-height: 1.5;
        }

        /* Detail Grid */
        .al-detail-grid {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 16px;
        }
        .al-detail-card {
          display: flex; align-items: center; gap: 14px; padding: 16px;
          background: white; border-radius: 12px; border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .al-detail-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -6px rgba(0, 0, 0, 0.08); }
        .al-detail-card-icon {
          width: 42px; height: 42px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
        }
        .al-detail-card-icon-blue  { background: #eff6ff; color: #3b82f6; }
        .al-detail-card-icon-amber { background: #fef3c7; color: #d97706; }
        .al-detail-card-icon-cyan  { background: #cffafe; color: #0891b2; }
        .al-detail-card-content { flex-grow: 1; min-width: 0; }
        .al-detail-card-label {
          font-size: 0.68rem; color: #94a3b8; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .al-detail-card-value { font-weight: 700; color: #0f172a; font-size: 0.85rem; }
        .al-value-cyan { color: #0891b2; }
        .al-value-amber { color: #d97706; }

        /* Detail Section */
        .al-detail-section {
          background: white; border-radius: 12px; border: 1px solid #e2e8f0; overflow: hidden;
        }
        .al-detail-section-header {
          display: flex; align-items: center; gap: 8px; padding: 14px 18px;
          background: #f8fafc; border-bottom: 1px solid #e2e8f0;
          font-weight: 700; color: #0f172a; font-size: 0.85rem;
        }
        .al-detail-section-header i { color: #f59e0b; font-size: 1rem; }
        .al-detail-section-body { padding: 16px 18px; }
        .al-code-block {
          background: #0f172a; color: #e2e8f0; padding: 16px;
          border-radius: 8px; font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-size: 0.75rem; overflow-x: auto; margin: 0;
          line-height: 1.6;
        }

        /* Tech rows */
        .al-tech-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 0; border-bottom: 1px solid #f1f5f9;
        }
        .al-tech-row:last-child { border-bottom: none; }
        .al-tech-label {
          font-size: 0.78rem; color: #94a3b8; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.03em;
        }
        .al-tech-value {
          font-size: 0.85rem; color: #0f172a; font-weight: 600;
          font-family: 'SF Mono', monospace;
        }

        /* ========== ANIMATIONS ========== */
        .al-spin { animation: al-spin 1s linear infinite; display: inline-block; }
        @keyframes al-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .al-detail-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .al-header { padding: 20px; }
          .al-header-title { font-size: 1.2rem; }
          .al-pagination { flex-direction: column; }
          .al-stat-value { font-size: 1.15rem; }
          .al-stat-icon-wrapper { width: 36px; height: 36px; font-size: 16px; }
          .al-modal-body { padding: 16px; }
          .al-detail-hero { padding: 16px; }
          .al-detail-avatar { width: 52px; height: 52px; font-size: 20px; }
        }
      `}</style>
    </div>
  );
}